import envConfig from "../config.js";

let cachedToken = null;
let cachedFingerprint = null;
let tokenExpiry = null;
let isFetchingToken = false;
let pendingTokenResolvers = [];

const generateFingerprint = () => {
  return Math.random().toString(36).substr(2, 12); // Increased length for better uniqueness
};

const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) throw new Error("Invalid token format");
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

const isTokenExpiringSoon = (expiry, bufferMs = 300000) => {
  // 5 minutes buffer
  return Date.now() >= expiry - bufferMs;
};

const fetchNewGuestToken = async () => {
  // Double-check to prevent race conditions
  if (isFetchingToken) {
    return new Promise((resolve, reject) => {
      pendingTokenResolvers.push({ resolve, reject });
    });
  }

  isFetchingToken = true;

  try {
    // Generate fingerprint if not exists
    if (!cachedFingerprint) {
      cachedFingerprint = generateFingerprint();
    }

    const headers = {
      accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": envConfig.CVS_API_KEY,
      "x-client-fingerprint-id": cachedFingerprint,
      cookie: `token_type=Bearer; fpcuid=${cachedFingerprint}`,
    };

    const response = await fetch(
      `${envConfig.GUEST_TOKEN_API_URL}?type=guesttoken`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({}),
        timeout: 10000, // 10 second timeout
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        "Failed to fetch guest token:",
        response.status,
        response.statusText,
        errorText
      );
      throw new Error(
        `Error fetching guest token: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!data.access_token) {
      throw new Error("No access token in response");
    }

    const token = data.access_token;
    const decoded = decodeJwt(token);

    if (!decoded?.exp) {
      console.error("Invalid token received, missing `exp` field:", decoded);
      throw new Error("Invalid token received, missing expiration");
    }

    // Validate token expiry is in the future
    const expiryTime = decoded.exp * 1000;
    if (expiryTime <= Date.now()) {
      throw new Error("Received expired token");
    }

    cachedToken = token;
    tokenExpiry = expiryTime;

    // Store in window and cookie if available
    if (typeof window !== "undefined") {
      window.guestToken = cachedToken;
      window.guestFingerprint = cachedFingerprint;

      try {
        document.cookie = `fpcuid=${cachedFingerprint}; path=/; Secure; SameSite=Strict`;
      } catch (cookieError) {
        console.warn("Failed to set cookie:", cookieError);
      }
    }

    const result = { token, fingerprint: cachedFingerprint };

    // Resolve all pending promises
    pendingTokenResolvers.forEach(({ resolve }) => resolve(result));
    pendingTokenResolvers = [];

    return result;
  } catch (error) {
    console.error("Error fetching guest token:", error);

    // Reject all pending promises
    pendingTokenResolvers.forEach(({ reject }) => reject(error));
    pendingTokenResolvers = [];

    throw error;
  } finally {
    isFetchingToken = false;
  }
};

export const fetchGuestToken = async () => {
  // Check if we have a valid cached token
  if (cachedToken && tokenExpiry && !isTokenExpiringSoon(tokenExpiry)) {
    console.log("Using cached guest token");
    return { token: cachedToken, fingerprint: cachedFingerprint };
  }

  console.log("Cached token expired or missing, fetching new token...");
  return fetchNewGuestToken();
};

export const getGlobalHeaders = async () => {
  // Ensure we have a valid token
  if (
    !cachedToken ||
    !cachedFingerprint ||
    !tokenExpiry ||
    isTokenExpiringSoon(tokenExpiry)
  ) {
    console.log("Token missing or expired, fetching new token...");
    await fetchGuestToken();
  }

  if (!cachedToken || !cachedFingerprint) {
    throw new Error("Failed to obtain valid token and fingerprint");
  }

  return {
    Authorization: `Bearer ${cachedToken}`,
    "x-api-key": envConfig.CVS_API_KEY,
    "x-client-fingerprint-id": cachedFingerprint,
  };
};

// Optional: Add a method to clear cached tokens
export const clearGuestToken = () => {
  cachedToken = null;
  cachedFingerprint = null;
  tokenExpiry = null;

  if (typeof window !== "undefined") {
    delete window.guestToken;
    delete window.guestFingerprint;
  }
};
